/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box, Checkbox, List, ListItem, Radio, Stack, Typography } from '@mui/material';
import { useContext, type ReactNode } from 'react';

import { allFeaturesDisabled, allPluginsDisabled, conflictBelow, premiumFeatureTooltip } from '../../../constants/tooltips';
import { HighlightSearch } from '../../HighlightSearch/HighlightSearch';
import { PluginPremiumBadge, PluginDisabledBadge, PluginConflictBadge } from '../../PluginBadges/PluginBadges';
import { LabelledSwitch } from '../../LabelledSwitch/LabelledSwitch';
import { isCapability } from '../../../utils/isCapability/isCapability';
import type { PluginGroupItemProps } from './types';
import type { CapabilityParsed } from '../types';
import { GTMContext } from '../../../contexts/GTMContext';
import { GTM_EVENT_GROUP_TOGGLE } from '../../../constants/GTMEventNames';

export function PluginGroupItem( {
	children,
	group,
	onPluginSelectionChange,
	onGroupToggle,
	isSingleFeatureChoice,
	searchable
}: PluginGroupItemProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	const prefixId = isCapability( group ) ? 'capability' : 'group';
	const opacity = group.isDisabled ? 0.5 : 1;
	const cursor = group.isDisabled ? 'default' : 'pointer';
	const disabledTooltip = isCapability( group ) ? allFeaturesDisabled : allPluginsDisabled;

	const inputProps = {
		id: `${ prefixId }-${ group.id }`,
		value: group.name,
		checked: group.isSelected,
		disabled: group.isDisabled,
		onChange: () => onPluginSelectionChange( group )
	};

	function handleLabelledSwitchToggle( group: CapabilityParsed ) {
		onGroupToggle( group );

		triggerGTMEvent( {
			event: GTM_EVENT_GROUP_TOGGLE,
			group: group.name,
			view: 'advanced',
			active: !group.toggleValue
		} );
	}

	return (
		<ListItem>
			<Stack direction='column' alignItems='start'>
				<Stack direction='row' alignItems='center' gap='8px' className='plugin-group-item'>
					{ !isSingleFeatureChoice && ( <Checkbox { ...inputProps } indeterminate={ group.isSelectedIndeterminate }/> ) }
					{ isSingleFeatureChoice && ( <Radio { ...inputProps } /> ) }
					<Typography
						htmlFor={ `${ prefixId }-${ group.id }` }
						component='label'
						className='plugin-group-label'
						sx={ { opacity, cursor } }
					>
						{ searchable ? (
							<HighlightSearch style={ { overflow: 'hidden', textOverflow: 'ellipsis' } } title={ group.name }>
								{ group.name }
							</HighlightSearch>
						) : (
							<span title={ group.name }>
								{ group.name }
							</span>
						) }
					</Typography>
					{ group.isPremium && (
						isCapability( group ) && (
							<PluginPremiumBadge tooltipTitle={ premiumFeatureTooltip }/>
						) || (
							<PluginPremiumBadge onlyIcon tooltipTitle={ premiumFeatureTooltip }/>
						)
					) }
					{ group.isDisabled && <PluginDisabledBadge tooltipTitle={ disabledTooltip }/> }
					{ group.hasConflict && !group.isDisabled && <PluginConflictBadge tooltipTitle={ conflictBelow }/> }
					{ isCapability( group ) && group.hasToggle && (
						<Box marginLeft='auto'>
							<LabelledSwitch
								id={ `group-toggle-${ group.id }` }
								text={ group.toggleLabel! }
								active={ group.toggleValue! }
								onToggle={ () => handleLabelledSwitchToggle( group ) }
								width='65px'
							/>
						</Box>
					) }
				</Stack>
				<List className='plugin-list'>
					{ children }
				</List>
			</Stack>
		</ListItem>
	);
}
